import type { ReactNode } from 'react';

/* A deliberately tiny highlighter for the console's own snippets: comments,
   strings and a few keywords, in the wings panel's three inks. */

const RULES: Record<'ts' | 'sh' | 'yaml', RegExp> = {
  ts: /(\/\/[^\n]*)|('(?:[^'\\\n]|\\.)*'|"(?:[^"\\\n]|\\.)*"|`[^`]*`)|\b(import|from|const|let|await|async|try|finally|catch|new|return|if|else|for|of)\b/g,
  sh: /(#[^\n]*)|('(?:[^'\n])*'|"(?:[^"\\\n]|\\.)*")|(^|\s)(curl|npm|export|jq)(?=\s)/gm,
  // comments, double-quoted strings, and mapping keys (a word followed by ": " or a line end)
  yaml: /(#[^\n]*)|("(?:[^"\\\n]|\\.)*")|([\w-]+)(?=:(?:\s|$))/gm,
};

export function highlight(code: string, lang: 'ts' | 'sh' | 'yaml' | 'text'): ReactNode {
  if (lang === 'text') return code;
  const rule = new RegExp(RULES[lang].source, RULES[lang].flags);
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = rule.exec(code)) !== null) {
    if (m.index > last) out.push(code.slice(last, m.index));
    if (m[1]) out.push(<span key={i++} className="c">{m[1]}</span>);
    else if (m[2]) out.push(<span key={i++} className="s">{m[2]}</span>);
    else if ((lang === 'ts' || lang === 'yaml') && m[3]) out.push(<span key={i++} className="k">{m[3]}</span>);
    else if (lang === 'sh' && m[4]) {
      out.push(m[3] ?? '');
      out.push(<span key={i++} className="k">{m[4]}</span>);
    } else out.push(m[0]);
    last = m.index + m[0].length;
    if (m[0].length === 0) rule.lastIndex++;
  }
  if (last < code.length) out.push(code.slice(last));
  return out;
}

export function agentPrompt(origin: string): string {
  return `Step 1: Set up offstage

Read ${origin}/SKILL.md and follow its Quick Setup. It covers the API key handoff, installing the SDK, and running a first command on a real Mac. Ask the user only where the skill says to.

Every offstage call needs these two environment variables:

OFFSTAGE_API_KEY=<the team key the user gives you, starting with sk_offstage_ (older keys: oc_live_)>
OFFSTAGE_API_URL=${origin}

Step 2: Detect the project

Do not ask. package.json means JavaScript/TypeScript: use the offstage SDK. pyproject.toml or requirements.txt means Python: use the Python helper in the skill. Only a Package.swift, Makefile or build script, with no package.json or pyproject.toml: use the Python helper in the skill (it needs nothing installed). A Package.swift, Makefile or build script is what you will build on the Mac. The Macs have Xcode 27.2 and the iOS 27.2 simulator, so swift, xcodebuild, xcrun simctl, clang and make all work.

Step 3: Install and integrate

Install what the skill names for that language, matching the project's package manager and env loading. If the project already has agent, CI or build scripts, wire offstage in where macOS work happens (swift build, make, scripts that need macOS) and keep the existing conventions.

Step 4: Verify

Run the skill's verification script: create a Mac sandbox, run sw_vers -productVersion and echo "hello from a mac", print stdout and the exit code, and kill the sandbox in a finally block. Pass means the sandbox id starts with sbx_, the macOS version starts with 27, the second command prints hello from a mac, both exit codes are 0, and the kill finishes without an error (the script prints "killed <id>"). If it fails, match the symptom in the skill's Troubleshooting section, fix it, and re-run once.

Step 5: Report

One paragraph: what ran, on which macOS version, what you verified. Then offer one next step from the skill: build this project on a Mac, add a macOS step to its CI with the skill's CI script, or take a screenshot from a GUI session.`;
}

export function curlSnippet(origin: string): string {
  return `# Your team key is in OFFSTAGE_API_KEY (sk_offstage_…); the API is this console's origin
export OFFSTAGE_API_URL=${origin}

# Create a Mac sandbox (10 minute timeout)
SBX=$(curl -s -X POST $OFFSTAGE_API_URL/v1/sandboxes \\
  -H "Authorization: Bearer $OFFSTAGE_API_KEY" \\
  -H "content-type: application/json" \\
  -d '{"timeoutMs": 600000}' | jq -r .id)

# Run a command on it
curl -s -X POST $OFFSTAGE_API_URL/v1/sandboxes/$SBX/commands \\
  -H "Authorization: Bearer $OFFSTAGE_API_KEY" \\
  -H "content-type: application/json" \\
  -d '{"cmd": "sw_vers -productVersion && echo hello from a mac"}'

# Delete it
curl -s -X DELETE $OFFSTAGE_API_URL/v1/sandboxes/$SBX \\
  -H "Authorization: Bearer $OFFSTAGE_API_KEY"`;
}

/** Names the API outright, so the same lines work against a local plane or a tunnel as well as https://api.offstage.cloud. */
export function apiUrlLine(origin: string): string {
  return `export OFFSTAGE_API_URL=${origin}`;
}

/** The newest stable-named tarball, until /llms.txt names the versioned one (what the docs install). */
export function sdkTarball(origin: string): string {
  return `${origin}/sdk/offstage-cloud.tgz`;
}

export function sdkInstall(tarball: string): string {
  return `npm install ${tarball}`;
}

/**
 * A GitHub Actions workflow that runs on an offstage Mac: the job asks for
 * the offstage-macos label, once its organization is connected on the CI
 * page. The same file as the home page's and SKILL.md's.
 */
export const GITHUB_WORKFLOW = `# .github/workflows/ci.yml
on: [push, pull_request]
jobs:
  test:
    runs-on: offstage-macos
    steps:
      - uses: actions/checkout@v7
      - run: swift test`;

export const SDK_EXAMPLE = `import { Sandbox } from 'offstage-cloud';

const sandbox = await Sandbox.create(); // reads OFFSTAGE_API_KEY and OFFSTAGE_API_URL
try {
  const result = await sandbox.commands.run('sw_vers -productVersion');
  console.log(result.stdout.trim(), result.exitCode);
} finally {
  await sandbox.kill();
}`;
